import { z } from 'zod';
/**
 * Contracts for the admin "Платёжный сервис" history + test tool
 * (`GET /api/payment/requests`, `POST /api/payment/test`). These DTOs are the
 * single source of truth shared by the admin API routes and the admin-web
 * module. The history surfaces every logged payment-service request
 * (`payment_request_log`); the test tool runs a request through the SAME logged
 * port the production rail uses — so a test is logged identically and exercises
 * the exact same HTTP adapter + mapping + error handling.
 *
 * IMPORTANT: `summary` / `result` carry compact derived shapes (counts, status,
 * aggregates) and money in **cents** — never the raw upstream response or any
 * secret. Money is formatted only at the UI edge.
 */
/** Result of a single logged payment request. */
export const PaymentLogResult = z.enum(['ok', 'error']);
/**
 * The payment operations the test tool can dispatch. Mirrors the `PaymentPort`
 * methods 1:1. Read ops (`billing_summary` / `check_active` /
 * `cancellation_state`) are gated `subscription.read`; cancel ops additionally
 * require `payment.admin_ops` (owner/supervisor) + audited (a test cancel is a
 * REAL cancel). NOT the member-plus `subscription.cancel` — that one covers only
 * the contact of the conversation the operator has open, while this tool takes
 * an arbitrary external id typed by hand.
 */
export const PaymentOperation = z.enum([
    'billing_summary',
    'check_active',
    'cancellation_state',
    'cancel_all',
    'cancel',
    'cancel_by_stripe_id',
    'cancel_all_by_customer',
]);
/**
 * One row of the payment request log, surfaced in the admin history.
 * - `summary` is a compact derived object (e.g. `{activeCount,cancelledCount}`
 *   for billing-summary, the aggregate for cancel-all) — loose by design.
 * - `actor` is `null` for reads; cancels carry `test:<id>` / `admin:<id>`.
 */
export const PaymentRequestLogDTO = z.object({
    id: z.string(),
    operation: z.string(),
    targetId: z.string(),
    actor: z.string().nullable(),
    result: PaymentLogResult,
    httpStatus: z.number().nullable(),
    summary: z.unknown().nullable(),
    errorMessage: z.string().nullable(),
    durationMs: z.number().nullable(),
    createdAt: z.string(),
});
/**
 * Cursor for `GET /api/payment/requests` paging. The log is ordered
 * `(created_at DESC, id DESC)`; the cursor is an opaque composite of the last
 * seen row's `created_at` + `id` so paging is stable even when several rows
 * share a timestamp. Treat it as opaque on the client — pass it back verbatim.
 * Format: `${createdAtISO}|${id}`.
 */
export const PaymentRequestListQuery = z.object({
    targetId: z.string().trim().min(1).optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
});
/**
 * Response for `GET /api/payment/requests` — newest-first, page-bounded.
 * `nextCursor` is non-null when more rows exist beyond this page; pass it back
 * as `?cursor=` to fetch the next page. `null` means this is the last page.
 */
export const PaymentRequestListResponse = z.object({
    items: z.array(PaymentRequestLogDTO),
    nextCursor: z.string().nullable(),
});
/**
 * Request body for `POST /api/payment/test`.
 * - The target is resolved per operation: explicit `userId` (or `email` →
 *   identity probe → wit user id) for user-scoped ops; `subscriptionId` for
 *   `cancel`; `stripeCustomerId` for `cancel_all_by_customer`; the upstream
 *   Stripe subscription id (carried in `subscriptionId`) for `cancel_by_stripe_id`.
 * - `reason` is attributed on cancel ops (logged + audited).
 */
export const PaymentTestRequest = z.object({
    operation: PaymentOperation,
    userId: z.string().trim().min(1).optional(),
    email: z.string().trim().email().optional(),
    subscriptionId: z.string().trim().min(1).optional(),
    stripeCustomerId: z.string().trim().min(1).optional(),
    reason: z.string().trim().min(1).max(500).optional(),
});
/**
 * Response for `POST /api/payment/test` (flat + explicit, always HTTP 200 so
 * the UI renders every case cleanly):
 *   - `configured: false`                          → payment not wired on server.
 *   - `resolvedUserId: null, error: <msg>`         → target couldn't be resolved.
 *   - `result: <domain result>, error: null`       → the op's domain result
 *     (billing summary / check / cancellation state / cancel aggregate / …).
 *   - `result: null, error: <msg>`                 → upstream call failed.
 * `result` is loose (z.unknown) because it carries one of several domain shapes
 * keyed by the requested operation; the UI narrows by the operation it sent.
 */
export const PaymentTestResponse = z.object({
    configured: z.boolean(),
    resolvedUserId: z.string().nullable(),
    result: z.unknown().nullable(),
    error: z.string().nullable(),
});
/**
 * Response for `GET /api/conversations/:id/stripe-customers` — the Stripe
 * customer id(s) behind this conversation's contact, resolved by searching
 * billing for every email the contact is known under (see
 * `lookupStripeCustomers` / `selectStripeCustomerIds` in admin-api). Empty
 * `ids` covers «nothing to search» and «several candidates, refusing to
 * guess» — same conservative rule as the automatic identity resolver.
 * `truncated` mirrors `CustomerSearchResult.truncated` from ANY of the
 * per-email searches — an incomplete answer for one address makes the whole
 * list incomplete.
 *
 * `unavailable: true` means at least one per-email search itself could not be
 * performed (e.g. the billing route is not deployed yet) — `ids` is then
 * ALWAYS `[]`, but that empty list must NOT be read as «no Stripe id known»:
 * the UI hides the whole Stripe line rather than asserting a negative on
 * incomplete information (same discriminated-answer principle as
 * `IdentitySearchResponse`'s `status: 'unavailable'`).
 */
export const StripeCustomersResponse = z.object({
    ids: z.array(z.string()),
    truncated: z.boolean(),
    unavailable: z.boolean(),
});
//# sourceMappingURL=payment.js.map